
import { defineComponent, onBeforeUnmount, reactive, toRefs } from "vue";
import { Temporal } from "@js-temporal/polyfill";
import { Sensor } from "@/interfaces/Sensor";
import { Data } from "@/interfaces/Data";
import { Burst } from "@/interfaces/Burst";
import { fetchSensors, fetchBursts, fetchData } from "@/helpers/api";
import { getUserTimeZone } from "@/helpers/store";

const red = "rgba(255, 0, 0, 0.7)";
const orange = "rgba(255, 140, 0, 0.7)";
const green = "rgba(0, 150, 0, 0.7)";
// const blue = "rgba(0, 100, 255, 0.7)";
// const purple = "rgba(255, 100, 255, 0.7)";
const grey = "rgba(54, 54, 54, 0.7)";

interface LiveState {
  loading: boolean;
  sensors: Sensor[];
  n_datapoints: number;
  history_minutes: number;
  too_many_points: boolean;
  plotHeight: string;
  updateData: number | undefined;
}

const categories = {
  healthy: { color: green },
  alert: { color: red },
  warning: { color: orange },
  offline: { color: grey, hoverable: false },
};

const get_line_label = (sensor_name: string, sensor_tags: Record<string, string>) => {
  let name = sensor_name;
  if (sensor_name.toLowerCase().includes("gps") || sensor_name.toLowerCase().includes("location")) {
    // One GPS appears as loads of sensors, e.g. "GPS Location - Latitude" and "GPS Location - Speed"
    // - we want to merge them all onto one line with a common name.  The " - Latitude" etc suffixes
    // are hard coded into aggregator/internal/daemons/location_logger.go so we can match on them.
    // The sensor values we display in the tooltips won't be very useful as you won't know what field
    // they represent, but we can fix that later if anyone notices it.
    [" - Latitude", " - Longitude", " - Altitude", " - Speed", " - Direction"].forEach(function (s) {
      name = name.replace(s, "");
    });
  }
  if (sensor_tags.Aggregator !== undefined) {
    return sensor_tags.Aggregator + " - " + name;
  } else {
    return name;
  }
};

const categorise = (datapoint: Data) =>
  datapoint.Alert ? "alert" : datapoint.Warning ? "warning" : datapoint.Value === null ? "offline" : "healthy";

function generate_label(value: number, tags: Record<string, string>) {
  if (value === null) {
    return "sensor not working";
  }
  if (tags.Datatype == "boolean") {
    if (value > 0) {
      return tags.TrueLabel === undefined ? "true" : tags.TrueLabel;
    } else {
      return tags.FalseLabel === undefined ? "false" : tags.FalseLabel;
    }
  }
  if (tags.Units !== undefined) {
    return value.toPrecision(4) + " " + tags.Units;
  }
  return value.toPrecision(4);
}

export default defineComponent({
  setup() {
    const state = reactive({
      loading: true,
      sensors: [],
      n_datapoints: 0,
      history_minutes: 30,
      too_many_points: false,
      plotHeight: "200px",
      updateData: undefined,
    } as LiveState);

    const load_sensors = async (): Promise<void> => {
      const res = await fetchSensors();
      state.sensors = res;
      load_data();
      state.updateData = setInterval(load_data, 30000);
    };

    const load_data = async (): Promise<void> => {
      // moment.subtract doesn't seem to mind if history_minutes is a string (which it will be, from the select values)
      const timezone = getUserTimeZone().value;
      const start_time = Temporal.Now.zonedDateTimeISO(timezone).subtract({
        minutes: state.history_minutes,
      });

      const burstsParams = new URLSearchParams();
      burstsParams.set("Limit", "1000");
      burstsParams.set("StartTime", String(start_time.epochSeconds));
      const [data, bursts] = [await fetchData(start_time.toInstant()), await fetchBursts(burstsParams)];

      state.too_many_points = data.length >= 1000 || bursts.length >= 1000;
      state.n_datapoints = data.length + bursts.length;
      plot(data, bursts, state.sensors, start_time.epochMilliseconds);
      state.loading = false;
    };

    const plot = (data: Data[], bursts: Burst[], sensors: Sensor[], start_time: number) => {
      const lines = sensors
        .map((s) => get_line_label(s.Name, s.Tags))
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort()
        .reverse(); // deduplicate

      state.plotHeight = lines.length + "em";

      const datapoints = data
        .map((d) => ({
          time: Temporal.Instant.from(d.Time).epochMilliseconds,
          line: get_line_label(d.SensorName, d.SensorTags),
          category: categorise(d),
          label: generate_label(d.Value, d.SensorTags),
        }))
        .concat(
          bursts.map((burst) => {
            const duration_seconds = Math.floor(burst.NSamples / burst.SampleRate);
            return {
              time: Temporal.Instant.from(burst.StartTime).epochMilliseconds,
              endTime: Temporal.Instant.from(burst.StartTime).add(
                new Temporal.Duration(0, 0, 0, 0, 0, 0, duration_seconds)
              ).epochMilliseconds,
              line: get_line_label(burst.SensorName, burst.SensorTags),
              category: "healthy",
              label: `${duration_seconds}s ${burst.Datatype} burst at ${burst.SampleRate} Hz`,
            };
          })
        );

      const options = {
        categories: categories,
        lines: lines,
        lineLabels: [],
        startTime: start_time,
        endTime: Temporal.Now.instant().epochMilliseconds,
      };
      // eslint-disable-next-line no-undef
      Timeline("#placeholder", datapoints, options);
    };
    onBeforeUnmount(() => clearInterval(state.updateData));
    load_sensors();
    return {
      ...toRefs(state),
      load_sensors,
    };
  },
});
